import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  Buildings, SquaresFour, FileText, Package, Receipt, SignOut, Sun, Moon, List as ListIcon, Gear, Truck, CreditCard,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, tid: "nav-dashboard" },
  { to: "/leases", label: "Leases", icon: FileText, tid: "nav-leases" },
  { to: "/assets", label: "Assets", icon: Package, tid: "nav-assets" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, tid: "nav-suppliers" },
  { to: "/payments", label: "Payments", icon: CreditCard, tid: "nav-payments" },
  { to: "/expenses", label: "Expenses", icon: Receipt, tid: "nav-expenses" },
  { to: "/settings", label: "Settings", icon: Gear, tid: "nav-settings", adminOnly: true },
];

function NavItems({ onNavigate, role }) {
  return (
    <>
      {NAV.filter((n) => !n.adminOnly || role === "admin").map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid} onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }>
          <n.icon size={18} weight="regular" />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 bg-primary flex items-center justify-center">
        <Buildings size={20} weight="bold" className="text-primary-foreground" />
      </div>
      <span className="font-display font-bold text-lg tracking-tighter">LeaseLedger</span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen grain flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="w-60 shrink-0 border-r border-border hidden md:flex flex-col fixed inset-y-0 z-20 bg-card">
        <div className="h-16 flex items-center px-6 border-b border-border"><Brand /></div>
        <nav className="flex-1 px-3 py-6 space-y-1"><NavItems role={user?.role} /></nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 text-xs text-muted-foreground truncate" data-testid="current-user">{user?.email}</div>
          <Button variant="ghost" onClick={doLogout} data-testid="logout-btn"
            className="w-full justify-start gap-3 rounded-sm text-muted-foreground hover:text-destructive">
            <SignOut size={18} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-60 min-w-0">
        <header className="h-16 border-b border-border sticky top-0 z-10 backdrop-blur-xl bg-background/70 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="h-9 w-9 flex items-center justify-center border border-border rounded-sm hover:bg-secondary transition-colors" data-testid="mobile-menu-btn">
                  <ListIcon size={18} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-card p-0">
                <div className="h-16 flex items-center px-6 border-b border-border"><Brand /></div>
                <nav className="px-3 py-6 space-y-1"><NavItems role={user?.role} onNavigate={() => setMobileOpen(false)} /></nav>
                <div className="p-3 border-t border-border absolute bottom-0 inset-x-0">
                  <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
                  <Button variant="ghost" onClick={doLogout} data-testid="logout-btn-mobile"
                    className="w-full justify-start gap-3 rounded-sm text-muted-foreground hover:text-destructive">
                    <SignOut size={18} /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <Brand />
          </div>
          <div className="flex-1" />
          <button onClick={toggle} data-testid="theme-toggle"
            className="h-9 w-9 flex items-center justify-center border border-border rounded-sm hover:bg-secondary transition-colors">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </header>
        <main className="p-6 lg:p-8 relative z-[2] max-w-[1500px]">{children}</main>
      </div>
    </div>
  );
}
